// Pure URL & Hub string helpers

export const slug = (s) =>
  String(s || '')
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)+/g, '');

export const extractName = (val) => {
  if (!val) return '';
  if (typeof val === 'string') return val;
  if (typeof val === 'number') return String(val);
  if (typeof val === 'object') {
    if (val.name) return String(val.name);
    if (val.title) return String(val.title);
    if (val.label) return String(val.label);
    if (val.hubName) return String(val.hubName);
    if (val.locationName) return String(val.locationName);
    if (val.town) return String(val.town);
    if (val.state) return String(val.state);
    if (val.slug) return String(val.slug);
    return '';
  }
  return String(val || '');
};

export const extractId = (val) => {
  if (!val) return null;
  if (typeof val === 'string' || typeof val === 'number') return String(val);
  if (typeof val === 'object') {
    const rawId = val.id !== undefined ? val.id : (val._id !== undefined ? val._id : null);
    return rawId !== null ? String(rawId) : null;
  }
  return null;
};

export const hubKey = (sub, st, town) =>
  `/c/${slug(sub)}/${slug(st)}${town ? '/' + slug(town) : ''}`;

export const countFor = (sub, st, town, retreatsList = []) => {
  if (!Array.isArray(retreatsList)) return 0;
  return retreatsList.filter(
    (r) => r.subCategory?.name === sub && r.location?.state === st && (!town || r.location?.city === town)
  ).length;
};

export const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];

// Helper to compute related hubs safely from real retreat data
export const computeRelatedHubs = ({
  category,
  subCategory,
  state,
  town,
  month,
  removedSet = new Set(),
  addedList = [],
  retreatsList = []
}) => {
  const safeList = Array.isArray(retreatsList) ? retreatsList : [];
  const catStr = extractName(category);
  const subStr = extractName(subCategory);
  const stStr = extractName(state);
  const townStr = extractName(town);
  const sub = subStr && !subStr.startsWith('All') ? subStr : catStr;
  const groups = { similar: [], nearby: [], statePop: [], explore: [] };

  // 1. Similar: same category/subcategory in other towns/states
  const seen = new Set();
  safeList
    .filter((r) => r.subCategory?.name === sub && !(r.location?.state === stStr && (!townStr || r.location?.city === townStr)))
    .forEach((r) => {
      const k = townStr ? hubKey(sub, r.location?.state, r.location?.city) : hubKey(sub, r.location?.state);
      if (seen.has(k)) return;
      seen.add(k);
      groups.similar.push({
        t: `${sub} in ${townStr ? r.location?.city + ', ' + r.location?.state : r.location?.state}`,
        u: k,
        n: townStr ? countFor(sub, r.location?.state, r.location?.city, safeList) : countFor(sub, r.location?.state, '', safeList),
        g: 'similar'
      });
    });

  // 2. Nearby: other categories in the same town/state
  const seen2 = new Set();
  safeList
    .filter((r) => r.subCategory?.name !== sub && r.location?.state === stStr && (!townStr || r.location?.city === townStr))
    .forEach((r) => {
      const k = hubKey(r.subCategory?.name, stStr, townStr);
      if (seen2.has(k)) return;
      seen2.add(k);
      groups.nearby.push({
        t: `${r.subCategory?.name} in ${townStr || stStr}`,
        u: k,
        n: countFor(r.subCategory?.name, stStr, townStr, safeList),
        g: 'nearby'
      });
    });

  // 3. Explore: up the hierarchy
  if (townStr) {
    groups.explore.push({
      t: `${sub} in ${stStr}`,
      u: hubKey(sub, stStr),
      n: countFor(sub, stStr, '', safeList),
      g: 'explore'
    });
  }
  groups.explore.push({
    t: `All ${catStr} in ${stStr}`,
    u: `/c/${slug(catStr)}/${slug(stStr)}`,
    n: safeList.filter((r) => r.category?.name === catStr && r.location?.state === stStr).length,
    g: 'explore'
  });
  groups.explore.push({
    t: `${sub}, all India`,
    u: `/c/${slug(sub)}`,
    n: safeList.filter((r) => r.subCategory?.name === sub).length,
    g: 'explore'
  });
  groups.explore.push({
    t: `${catStr}, all India`,
    u: `/h/${slug(catStr)}`,
    n: safeList.filter((r) => r.category?.name === catStr).length,
    g: 'explore'
  });
  if (month && MONTHS.includes(month)) {
    groups.explore.push({
      t: `${sub} in ${month}`,
      u: `/c/${slug(sub)}/${slug(month)}`,
      n: safeList.filter(
        (r) => r.subCategory?.name === sub && Array.isArray(r.months) && r.months.includes(MONTHS.indexOf(month) + 1)
      ).length,
      g: 'explore'
    });
  }

  for (const g in groups) {
    groups[g] = groups[g].filter((x) => x.n > 0 && !removedSet.has(x.u)).slice(0, 6);
  }

  if (Array.isArray(addedList)) {
    addedList.forEach((x) => {
      if (groups[x.g]) groups[x.g].push(x);
    });
  }

  return groups;
};

// Reusable standard input styling per AGENTS.md
export const standardInputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '6px',
    fontSize: 12.5,
    backgroundColor: '#FFFFFF',
    fontFamily: 'inherit',
    minHeight: 34,
    height: 34,
    '& fieldset': { borderColor: '#E2E8F0' },
    '&:hover fieldset': { borderColor: '#FFBD65' },
    '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' },
  },
  '& .MuiOutlinedInput-input': {
    py: '6px',
    px: '10px',
    fontSize: 12.5,
    color: '#1E293B',
    fontFamily: 'inherit',
  },
  '& .MuiSelect-select': {
    py: '6px',
    px: '10px',
    fontSize: 12.5,
    color: '#1E293B',
    fontFamily: 'inherit',
    display: 'flex',
    alignItems: 'center',
  },
  '& .MuiInputLabel-root': {
    fontSize: 12,
    color: '#64748B',
    fontFamily: 'inherit',
    '&.Mui-focused': { color: '#803927' }
  },
  '& .MuiFormHelperText-root': {
    fontSize: 10.5,
    color: '#64748B',
    fontFamily: 'inherit',
    mx: 0,
    mt: 0.35,
    lineHeight: 1.3
  }
};
